import gsap from 'gsap';
import { markPageReady, markPageTransitioning } from '../utils/PageLifecycle';

const INTRO_VIDEO_SESSION_KEY = 'stablevine-transition-video-played';

export default class PageTransition {
  constructor() {
    this.overlay = document.querySelector('.page-transition-overlay');
    if (!this.overlay) {
      markPageReady('no-overlay');
      return;
    }
    
    this.video = this.overlay.querySelector('video');
    this.isAnimating = false;
    this.revealTimeout = null;
    this.overlaySlideDuration = 0.65;
    this.shouldPlayIntroVideo = this.shouldPlayIntroVideoOnce();

    this.init();
  }

  init() {
    // The transition needs a parsed page, not every image on the page. Waiting
    // for window.load kept the overlay in place until below-the-fold media had
    // finished loading. Queue the reveal for the next frame after DOM readiness
    // so layout is available while non-critical media continues in the background.
    const revealWhenReady = () => {
      window.requestAnimationFrame(() => this.revealPage());
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', revealWhenReady, { once: true });
    } else {
      revealWhenReady();
    }

    // Handle links
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      if (this.shouldTransition(link, e)) {
        e.preventDefault();
        if (!this.isAnimating) {
          this.hidePage(link.href);
        }
      }
    });
    
    // Handle browser back/forward buttons
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.overlay.classList.add('is-hidden');
        gsap.set(this.overlay, { y: '100%' }); // Ensure it's out of view
        this.isAnimating = false;
        markPageReady('bfcache');
      }
    });
  }

  shouldTransition(link, e) {
    // 1. Check if it opens in new tab
    if (link.target === '_blank') return false;
    
    // 2. Check if it's an anchor link on the same page
    if (link.href.indexOf('#') !== -1 && link.pathname === window.location.pathname) return false;
    
    // 3. Check if it's a different domain
    if (link.hostname !== window.location.hostname) return false;
    
    // 4. Check for modifier keys (cmd/ctrl + click)
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return false;
    
    // 5. Check if it's a mailto or tel link
    if (link.href.indexOf('mailto:') !== -1 || link.href.indexOf('tel:') !== -1) return false;
    
    // 6. Check if it's a file download
    const fileExtensions = ['.pdf', '.doc', '.docx', '.zip', '.jpg', '.png', '.mp4'];
    if (fileExtensions.some(ext => link.pathname.endsWith(ext))) return false;

    return true;
  }

  revealPage() {
    this.isAnimating = true;
    this.overlay.classList.remove('is-hidden');
    gsap.set(this.overlay, { y: '0%' });

    // The facade film is an introduction, not a cost paid on every internal
    // navigation. Later pages retain the same green overlay motion, without
    // adding video sources to the browser's request queue.
    if (!this.shouldPlayIntroVideo) {
      this.slideOverlayOut();
      return;
    }

    this.loadIntroVideoSources();

    this.playVideo().finally(() => {
      this.markIntroVideoPlayed();
      this.slideOverlayOut();
    });
  }

  shouldPlayIntroVideoOnce() {
    try {
      return window.sessionStorage.getItem(INTRO_VIDEO_SESSION_KEY) !== 'true';
    } catch (error) {
      // If storage is unavailable, retain the branded introduction rather than
      // risk suppressing the transition altogether.
      return true;
    }
  }

  markIntroVideoPlayed() {
    try {
      window.sessionStorage.setItem(INTRO_VIDEO_SESSION_KEY, 'true');
    } catch (error) {
      // Storage can be disabled by privacy settings; the transition still works.
    }
  }

  loadIntroVideoSources() {
    if (!this.video || this.video.querySelector('source')) return;

    const sources = [
      { src: this.video.dataset.hevcSrc, type: 'video/mp4; codecs=hvc1' },
      { src: this.video.dataset.webmSrc, type: 'video/webm' }
    ];

    sources.forEach(({ src, type }) => {
      if (!src) return;

      const source = document.createElement('source');
      source.src = src;
      source.type = type;
      this.video.appendChild(source);
    });

    this.video.load();
  }

  hidePage(href) {
    this.isAnimating = true;
    markPageTransitioning('page-transition');
    this.overlay.classList.remove('is-hidden');
    
    // Slide down from top (-100% -> 0%)
    gsap.fromTo(this.overlay,
      { y: '-100%' },
      {
        y: '0%',
        duration: this.overlaySlideDuration,
        ease: 'power3.inOut',
        onComplete: () => {
          window.location.href = href;
        }
      }
    );
  }

  playVideo() {
    if (!this.video) {
      return Promise.resolve();
    }

    clearTimeout(this.revealTimeout);

    return new Promise((resolve) => {
      let isFinished = false;

      const finish = () => {
        if (isFinished) return;
        isFinished = true;

        clearTimeout(this.revealTimeout);
        this.video.onended = null;

        // Keep the completed facade frame visible while the whole green overlay exits.
        if (Number.isFinite(this.video.duration) && this.video.duration > 0) {
          const holdTime = Math.max(this.video.duration - 0.05, 0);

          if (Math.abs(this.video.currentTime - holdTime) > 0.02) {
            this.video.addEventListener('seeked', resolve, { once: true });
            this.video.currentTime = holdTime;
            return;
          }
        }

        resolve();
      };

      this.revealTimeout = setTimeout(finish, 3000);
      this.video.onended = finish;
      this.video.currentTime = 0;

      const playPromise = this.video.play();

      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Video playback failed:', error);
          finish();
        });
      }
    });
  }

  slideOverlayOut() {
    gsap.to(this.overlay, {
      y: '100%',
      duration: this.overlaySlideDuration,
      ease: 'power3.inOut',
      onStart: () => {
        // Start page animations as the overlay exits so the page is already moving when revealed.
        gsap.delayedCall(0.3, () => markPageReady('page-transition-reveal'));
      },
      onComplete: () => {
        this.overlay.classList.add('is-hidden');
        this.isAnimating = false;
        markPageReady('page-transition');

        if (this.video) {
          this.video.pause();
          this.video.currentTime = 0;
        }
      }
    });
  }
}
